import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Briefcase, Square, Play, Bell } from "lucide-react";
import { Button, Card, Spinner } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingOut, setClockingOut] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (e) {
      console.error('markAsRead failed:', e);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (e) {
      console.error('markAllAsRead failed:', e);
    }
  };

  // Check if notification is a shift duration alert
  const isShiftAlert = (notification) => {
    const title = notification.title?.toLowerCase() || '';
    return title.includes('hour') && title.includes('clocked');
  };

  // Handle clock out from notification
  const handleClockOut = async (notificationId) => {
    setClockingOut(true);
    try {
      await axios.post(`${API}/time/clock-out`);
      await markAsRead(notificationId);
      alert("Successfully clocked out!");
      fetchNotifications();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockingOut(false);
    }
  };

  // Handle continue working (just mark as read)
  const handleContinueWorking = async (notificationId) => {
    await markAsRead(notificationId);
  };

  const typeIcons = {
    info: <AlertCircle className="w-5 h-5 text-blue-600" />,
    warning: <AlertCircle className="w-5 h-5 text-amber-500" />,
    alert: <AlertCircle className="w-5 h-5 text-red-600" />,
    job_update: <Briefcase className="w-5 h-5 text-orange-500" />
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Notifications</h1>
        {notifications.some(n => !n.is_read) && (
          <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read-btn">
            Mark All Read
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {notifications.map(notification => (
          <Card 
            key={notification.id}
            className={`transition-colors ${!notification.is_read ? 'border-orange-500 bg-orange-50/50' : ''}`}
            data-testid={`notification-${notification.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">
                {typeIcons[notification.notification_type] || typeIcons.info}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold">{notification.title}</h3>
                  <span className="text-xs text-slate-500">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-600">{notification.message}</p>
                
                {/* Shift Duration Action Buttons */}
                {isShiftAlert(notification) && !notification.is_read && (
                  <div className="flex gap-3 mt-4 pt-3 border-t border-slate-200">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleClockOut(notification.id)}
                      disabled={clockingOut}
                      data-testid={`clock-out-btn-${notification.id}`}
                    >
                      <Square className="w-4 h-4 mr-2" />
                      {clockingOut ? 'Clocking Out...' : 'Clock Out Now'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleContinueWorking(notification.id)}
                      data-testid={`continue-btn-${notification.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Continue Working
                    </Button>
                  </div>
                )}
              </div>
              {!notification.is_read && !isShiftAlert(notification) && (
                <button
                  onClick={() => markAsRead(notification.id)}
                  className="text-xs text-orange-600 font-semibold hover:underline"
                >
                  Mark Read
                </button>
              )}
            </div>
          </Card>
        ))}

        {notifications.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold">No notifications</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
